import { Timeline } from '../__generated__/protobuf/sell/common/common_pb';

declare let window: any;

export type GTMEventProperties = Record<string, string | number | boolean>;

// Allowlist keeps PII (emails, phones, names, free text) out of the dataLayer;
// only ad-relevant fields may be forwarded to GTM.
const GTM_ALLOWED_PROPERTY_KEYS = ['addressUuid', 'priceCents', 'placement', 'source'] as const;

// Enum fields arrive as numbers; GTM gets the value name, the same string the warehouse stores.
const GTM_ALLOWED_ENUM_PROPERTIES: Record<string, Record<number, string>> = {
  saleTimeline: Timeline,
};

export function pickGTMEventProperties(eventData: unknown): GTMEventProperties | undefined {
  if (typeof eventData !== 'object' || eventData === null) return undefined;

  const properties: GTMEventProperties = {};
  let hasProperties = false;
  for (const key of GTM_ALLOWED_PROPERTY_KEYS) {
    const value: unknown = Reflect.get(eventData, key);
    if (
      (typeof value === 'string' && value !== '') ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      properties[key] = value;
      hasProperties = true;
    } else if (typeof value === 'bigint' && Number.isSafeInteger(Number(value))) {
      // Proto-generated types use bigint for int64 fields (e.g. priceCents), and
      // JSON.stringify throws on BigInt. Cent amounts are far below 2^53, so this is lossless.
      properties[key] = Number(value);
      hasProperties = true;
    }
  }
  for (const [key, enumNames] of Object.entries(GTM_ALLOWED_ENUM_PROPERTIES)) {
    const value: unknown = Reflect.get(eventData, key);
    const name = typeof value === 'number' ? enumNames[value] : undefined;
    if (name !== undefined) {
      properties[key] = name;
      hasProperties = true;
    }
  }
  return hasProperties ? properties : undefined;
}

/**
 * Pushes Journey event data to GTM's dataLayer.
 * GTM owns all ad-platform translation — this function just provides
 * the Journey event identity and shared event ID.
 *
 * Each ad platform has its own Regex Table Variable in GTM that
 * maps journey_category + journey_event_name to that platform's
 * standard event names. Adding a new platform requires zero code changes.
 */
export function trackJourneyEventInGTM(
  categoryName: string,
  eventName: string,
  clientEventId: string,
  eventData?: unknown,
): void {
  if (typeof window === 'undefined') return;

  const properties = pickGTMEventProperties(eventData);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'journey_event',
    journey_category: categoryName,
    journey_event_name: eventName,
    journey_event_id: clientEventId,
    ...(properties ? { journey_event_properties: properties } : {}),
  });
}
